import React, { useState, useEffect } from 'react';
import { Table } from '../components/ui/Table';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Card } from '../components/ui/Card';
import { RefreshCw, Edit, Check, X, Eye, EyeOff, DollarSign, Euro, PoundSterling, RotateCcw } from 'lucide-react';
import { listPackages, updatePackagePrices, revertPackagePriceToProvider, setPackageStatus, setPackageType } from '../services/api';

export const PackageManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [editingPackage, setEditingPackage] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    usdPrice: string;
    eurPrice: string;
    gbpPrice: string;
    mamsPrice: string;
    status: 'Active' | 'Hidden';
  }>({
    usdPrice: '',
    eurPrice: '',
    gbpPrice: '',
    mamsPrice: '',
    status: 'Active'
  });
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  // Load packages from API
  const loadPackages = async () => {
    setLoading(true);
    try {
      const result = await listPackages(
        filters.status?.length ? filters.status : undefined,
        filters.type?.length ? filters.type : undefined,
        searchTerm || undefined,
        page,
        pageSize,
        false
      );
      if (result && result.data) {
        setPackages(result.data);
        setTotal(result.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, [page, pageSize, searchTerm, filters]);
  // Filter groups for the search filter component
  const filterGroups = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: 'Active',
      label: 'Active'
    }, {
      id: 'Hidden',
      label: 'Hidden'
    }]
  }, {
    id: 'type',
    label: 'Type',
    options: [{
      id: 'esim',
      label: 'E-SIM'
    }, {
      id: 'topup',
      label: 'Top-up'
    }]
  }];
  // Handle edit package
  const handleEditPackage = (pkg: any) => {
    setEditingPackage(pkg.name);
    setEditForm({
      usdPrice: (pkg.gross_price_usd || 0).toString(),
      eurPrice: (pkg.gross_price_eur || 0).toString(),
      gbpPrice: (pkg.gross_price_gbp || 0).toString(),
      mamsPrice: (pkg.gross_price_mams || 0).toString(),
      status: pkg.status
    });
  };
  // Handle save package
  const handleSavePackage = async (id: string) => {
    try {
      const result = await updatePackagePrices({
        product: id,
        gross_usd: parseFloat(editForm.usdPrice) || undefined,
        gross_eur: parseFloat(editForm.eurPrice) || undefined,
        gross_gbp: parseFloat(editForm.gbpPrice) || undefined,
        gross_mams: parseFloat(editForm.mamsPrice) || undefined,
        status: editForm.status
      });
      
      if (result) {
        setEditingPackage(null);
        loadPackages(); // Reload packages
      }
    } catch (error) {
      console.error('Error saving package:', error);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingPackage(null);
  };

  // Handle reset to Airalo price
  const handleResetToAiralo = async (id: string) => {
    try {
      const result = await revertPackagePriceToProvider(id);
      if (result) {
        loadPackages(); // Reload packages
        setEditingPackage(null);
      }
    } catch (error) {
      console.error('Error resetting package:', error);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (id: string) => {
    try {
      const pkg = packages.find(p => p.name === id);
      const newStatus = pkg?.status === 'Active' ? 'Hidden' : 'Active';
      const result = await setPackageStatus(id, newStatus);
      if (result) {
        loadPackages(); // Reload packages
      }
    } catch (error) {
      console.error('Error updating package status:', error);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilters({});
    setPage(1);
    setEditingPackage(null); // Also reset editing state
  };
  // Table columns
  const columns = [{
    key: 'package_name',
    header: 'Package Name'
  }, {
    key: 'name',
    header: 'Package ID'
  }, {
    key: 'provider_price_usd',
    header: 'Net Price (USD)',
    render: (value: any) => `$${Number(value || 0).toFixed(2)}`
  }, {
    key: 'gross_price_usd',
    header: 'Gross Price (USD)',
    render: (value: any, row: any) => editingPackage === row.name ? <div className="flex items-center">
            <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
            <input type="number" step="0.01" min="0" value={editForm.usdPrice} onChange={e => setEditForm({
        ...editForm,
        usdPrice: e.target.value
      })} className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm" />
          </div> : `$${Number(value || 0).toFixed(2)}`
  }, {
    key: 'gross_price_eur',
    header: 'Gross Price (EUR)',
    render: (value: any, row: any) => editingPackage === row.name ? <div className="flex items-center">
            <Euro className="h-4 w-4 text-gray-400 mr-1" />
            <input type="number" step="0.01" min="0" value={editForm.eurPrice} onChange={e => setEditForm({
        ...editForm,
        eurPrice: e.target.value
      })} className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm" />
          </div> : `€${Number(value || 0).toFixed(2)}`
  }, {
    key: 'gross_price_gbp',
    header: 'Gross Price (GBP)',
    render: (value: any, row: any) => editingPackage === row.name ? <div className="flex items-center">
            <PoundSterling className="h-4 w-4 text-gray-400 mr-1" />
            <input type="number" step="0.01" min="0" value={editForm.gbpPrice} onChange={e => setEditForm({
        ...editForm,
        gbpPrice: e.target.value
      })} className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm" />
          </div> : `£${Number(value || 0).toFixed(2)}`
  }, {
    key: 'gross_price_mams',
    header: 'Gross Price (MaMs)',
    render: (value: any, row: any) => editingPackage === row.name ? <div className="flex items-center">
            <input type="number" min="0" value={editForm.mamsPrice} onChange={e => setEditForm({
        ...editForm,
        mamsPrice: e.target.value
      })} className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm" />
          </div> : `${Number(value || 0).toFixed(2)} MaMs`
  }, {
    key: 'status',
    header: 'Status',
    render: (value: string, row: any) => editingPackage === row.name ? <select value={editForm.status} onChange={e => setEditForm({
      ...editForm,
      status: e.target.value as 'Active' | 'Hidden'
    })} className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm">
            <option value="Active">Active</option>
            <option value="Hidden">Hidden</option>
          </select> : <span className={`px-2 py-1 rounded-full text-xs font-medium ${value === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
  }, {
    key: 'product_type',
    header: 'Type'
  }, {
    key: 'actions',
    header: 'Actions',
    render: (_: any, row: any) => editingPackage === row.name ? <div className="flex space-x-2">
            <button onClick={() => handleSavePackage(row.name)} className="text-green-600 hover:text-green-900" title="Save">
              <Check className="h-5 w-5" />
            </button>
            <button onClick={handleCancelEdit} className="text-red-600 hover:text-red-900" title="Cancel">
              <X className="h-5 w-5" />
            </button>
            <button onClick={() => handleResetToAiralo(row.name)} className="text-blue-600 hover:text-blue-900" title="Reset to Airalo price">
              <RefreshCw className="h-5 w-5" />
            </button>
          </div> : <div className="flex space-x-2">
            <button onClick={() => handleEditPackage(row)} className="text-indigo-600 hover:text-indigo-900" title="Edit">
              <Edit className="h-5 w-5" />
            </button>
            <button onClick={() => handleToggleStatus(row.name)} className={`${row.status === 'Active' ? 'text-gray-600 hover:text-gray-900' : 'text-green-600 hover:text-green-900'}`} title={row.status === 'Active' ? 'Hide' : 'Show'}>
              {row.status === 'Active' ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
  }];
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Package Management
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage e-sim and top-up packages
          </p>
        </div>
        <button 
          onClick={handleResetFilters}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset Filters
        </button>
      </div>
      <Card>
        <SearchFilter placeholder="Search by package name or ID..." filterGroups={filterGroups} onSearch={setSearchTerm} onFilter={setFilters} />
        <Table columns={columns} data={packages} isLoading={loading} emptyMessage="No packages found matching your search criteria." />
      </Card>
    </div>;
};